import React, { useEffect, useState, useRef } from 'react'
import ProductGrid from '../components/Products/ProductGrid';
import { SlidersHorizontal } from 'lucide-react';
import SortOptions from '../components/Products/SortOptions';
import { FaFilter } from 'react-icons/fa';
import FilterSidebar from "../components/Products/FilterSidebar";
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductsByFilters } from '../../redux/slices/productsSlice';

const CollectionPage = () => {
  const { collection }      = useParams();
  const [searchParams]      = useSearchParams();
  const dispatch            = useDispatch();
  const { products, loading, error } = useSelector((state) => state.products);
  const queryParams         = Object.fromEntries([...searchParams]);
  const sidebarRef          = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [groupedProducts,   setGroupedProducts]   = useState({});
  const [mainCategories,    setMainCategories]     = useState({});  // 2+ products
  const [singleProducts,    setSingleProducts]     = useState([]);  // exactly 1 product

  useEffect(() => {
    dispatch(fetchProductsByFilters({ collection, ...queryParams }));
  }, [dispatch, collection, searchParams]);

  useEffect(() => {
    if (products && products.length > 0) {
      // DEBUG logs — remove when stable
      console.log('First product FULL DATA:', products[0]);
      console.log('First 3 products:', products.slice(0, 3).map(p => ({
        name: p.name, category: p.category,
        updatedAt: p.updatedAt, createdAt: p.createdAt,
        hasUpdatedAt: !!p.updatedAt, hasCreatedAt: !!p.createdAt,
      })));
      const acidWashed = products.find(p => p.category?.includes('Acid'));
      if (acidWashed) {
        console.log('Acid Washed product dates:', {
          name: acidWashed.name, category: acidWashed.category,
          updatedAt: acidWashed.updatedAt, createdAt: acidWashed.createdAt,
          updatedDate: new Date(acidWashed.updatedAt),
          createdDate: new Date(acidWashed.createdAt),
        });
      }

      // Group by category
      const grouped = products.reduce((acc, product) => {
        const category = product.category || 'Uncategorized';
        if (!acc[category]) {
          acc[category] = {
            products: [],
            mostRecentUpdate: new Date(product.updatedAt || product.createdAt || 0),
          };
        }
        acc[category].products.push(product);
        const productDate = new Date(product.updatedAt || product.createdAt || 0);
        if (productDate > acc[category].mostRecentUpdate) {
          acc[category].mostRecentUpdate = productDate;
        }
        return acc;
      }, {});

      // Sort categories by most recent update
      const sortedGrouped = Object.fromEntries(
        Object.entries(grouped).sort((a, b) => b[1].mostRecentUpdate - a[1].mostRecentUpdate)
      );

      console.log('Categories sorted by update:', Object.keys(sortedGrouped));
      setGroupedProducts(sortedGrouped);

      // ── OPTION 2: split into main (≥2) and singles (=1) ──
      const main    = {};
      const singles = [];

      Object.entries(sortedGrouped).forEach(([category, data]) => {
        if (data.products.length >= 2) {
          main[category] = data;
        } else {
          singles.push(...data.products);
        }
      });

      setMainCategories(main);
      setSingleProducts(singles);
    }
  }, [products]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleClickOutside = (e) => {
    if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
      setIsSidebarOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatTitle = (name) => {
    if (!name) return { light: '', bold: 'All Collection' };
    const words = name.trim().split(' ');
    if (words.length === 1) return { light: '', bold: words[0] };
    return { light: words[0], bold: words.slice(1).join(' ') };
  };

  const { light, bold } = formatTitle(collection);
  const hasContent = Object.keys(mainCategories).length > 0 || singleProducts.length > 0;

  return (
    <>
      <style>{cpStyles}</style>

      <div className="cp-root">

        {/* ── COLLECTION HEADER ── */}
        <div className="cp-header">
          <div className="cp-header-left">
            <div className="cp-eyebrow">Collection</div>
            <h1 className="cp-title">
              {light && <span className="cp-title-light">{light} </span>}
              <span className="cp-title-bold">{bold}</span>
            </h1>
          </div>
          <div className="cp-header-right">
            <span className="cp-count">
              {products?.length || 0} {products?.length === 1 ? 'piece' : 'pieces'}
            </span>
            <SortOptions />
          </div>
        </div>

        <div className="cp-rule"><hr /></div>

        {/* ── LOADING ── */}
        {loading && (
          <div className="cp-state">
            <span className="cp-state-label">Loading</span>
          </div>
        )}

        {/* ── ERROR ── */}
        {error && (
          <div className="cp-state">
            <span className="cp-state-label">Something went wrong</span>
            <span className="cp-state-sub">{error}</span>
          </div>
        )}

        {/* ── PRODUCTS ── */}
        {!loading && !error && hasContent && (
          <div className="cp-sections">

            {/* MAIN CATEGORIES — 2+ products each, get full header treatment */}
            {Object.entries(mainCategories).map(([category, categoryData]) => {
              const { light: catLight, bold: catBold } = formatTitle(category);
              return (
                <div key={category} className="cp-section">
                  <div className="cp-cat-header">
                    <div>
                      <div className="cp-cat-eyebrow">Category</div>
                      <h2 className="cp-cat-title">
                        {catLight && <span className="cp-cat-light">{catLight} </span>}
                        <span className="cp-cat-bold">{catBold}</span>
                      </h2>
                    </div>
                    <span className="cp-cat-count">
                      {categoryData.products.length} {categoryData.products.length === 1 ? 'piece' : 'pieces'}
                    </span>
                  </div>
                  <div className="cp-cat-rule"><hr /></div>
                  <ProductGrid products={categoryData.products} loading={false} error={null} />
                </div>
              );
            })}

            {/* SINGLES — all lone products grouped under one minimal header */}
            {singleProducts.length > 0 && (
              <div className="cp-section">
                <div className="cp-cat-header">
                  <div>
                    <div className="cp-cat-eyebrow">Also Available</div>
                    <h2 className="cp-cat-title">
                      <span className="cp-cat-light">More </span>
                      <span className="cp-cat-bold">Pieces</span>
                    </h2>
                  </div>
                  <span className="cp-cat-count">
                    {singleProducts.length} {singleProducts.length === 1 ? 'piece' : 'pieces'}
                  </span>
                </div>
                <div className="cp-cat-rule"><hr /></div>
                <ProductGrid products={singleProducts} loading={false} error={null} />
              </div>
            )}

          </div>
        )}

        {/* ── EMPTY ── */}
        {!loading && !error && products?.length === 0 && (
          <div className="cp-state">
            <span className="cp-state-label">No products found</span>
            <span className="cp-state-sub">Try browsing a different collection</span>
          </div>
        )}

      </div>
    </>
  );
};

/* ── STYLES ── */
const cpStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  .cp-root {
    background: #f8f8f8;
    min-height: 100vh;
    padding: 72px 0 96px;
    font-family: 'Exo 2', sans-serif;
  }

  /* ── COLLECTION HEADER ── */
  .cp-header {
    max-width: 1400px;
    margin: 0 auto 36px;
    padding: 0 80px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
  }

  .cp-eyebrow {
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .cp-eyebrow::before {
    content: '';
    width: 24px;
    height: 1px;
    background: rgba(0,0,0,0.2);
    display: block;
    flex-shrink: 0;
  }

  .cp-title {
    font-size: clamp(32px, 4.5vw, 60px);
    line-height: 1;
    margin: 0;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .cp-title-light { font-weight: 200; color: black; }
  .cp-title-bold  { font-weight: 600; color: black; }

  .cp-header-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    padding-bottom: 4px;
    flex-shrink: 0;
  }

  .cp-count {
    font-size: 9px;
    font-weight: 300;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: black;
  }

  /* ── RULE ── */
  .cp-rule {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 80px;
  }
  .cp-rule hr {
    border: none;
    border-top: 1px solid black;
    margin: 0;
  }

  /* ── STATE ── */
  .cp-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 80px 0;
  }
  .cp-state-label {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: black;
  }
  .cp-state-sub {
    font-size: 10px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: black;
  }

  /* ── SECTIONS ── */
  .cp-sections {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 80px;
    display: flex;
    flex-direction: column;
    gap: 72px;
  }

  .cp-section { padding-top: 52px; }

  /* ── CATEGORY HEADER ── */
  .cp-cat-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 28px;
    gap: 16px;
  }

  .cp-cat-eyebrow {
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .cp-cat-eyebrow::before {
    content: '';
    width: 20px;
    height: 1px;
    background: rgba(0,0,0,0.15);
    display: block;
    flex-shrink: 0;
  }

  .cp-cat-title {
    font-size: clamp(22px, 3vw, 40px);
    line-height: 1;
    margin: 0;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .cp-cat-light { font-weight: 200; color: black; }
  .cp-cat-bold  { font-weight: 600; color: black; }

  .cp-cat-count {
    font-size: 9px;
    font-weight: 300;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: black;
    padding-bottom: 4px;
    flex-shrink: 0;
  }

  .cp-cat-rule hr {
    border: none;
    border-top: 1px solid black;
    margin: 0;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .cp-header   { padding: 0 40px; }
    .cp-rule     { padding: 0 40px; }
    .cp-sections { padding: 0 40px; }
  }

  @media (max-width: 768px) {
    .cp-root { padding: 48px 0 64px; }
    .cp-header {
      flex-direction: column;
      align-items: flex-start;
      padding: 0 20px;
      margin-bottom: 24px;
      gap: 16px;
    }
    .cp-header-right { align-items: flex-start; }
    .cp-rule     { padding: 0 20px; }
    .cp-sections { padding: 0 20px; gap: 48px; }
    .cp-section  { padding-top: 36px; }
    .cp-cat-header { flex-direction: column; align-items: flex-start; gap: 8px; }
    .cp-cat-count  { padding-bottom: 0; }
  }

  @media (max-width: 360px) {
    .cp-header   { padding: 0 14px; }
    .cp-rule     { padding: 0 14px; }
    .cp-sections { padding: 0 14px; }
  }
`;

export default CollectionPage;
